import { create } from 'zustand';
import { Auth, Hub } from 'aws-amplify';
import { AuthState, User } from '../types/store';

interface AuthStore extends AuthState {
  // Actions
  signIn: (user: User) => void;
  signOut: () => Promise<void>;
  setFirstTimeSignin: (value: boolean) => void;
  clearFirstTimeSignin: () => void;
  enableFirstTimeSignin: () => void;
  checkAuthStatus: () => Promise<void>;
  initialize: () => (() => void) | void;
  completeOnboarding: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial state
  isAuthenticated: false,
  isLoading: true,
  user: null,
  firstTimeSignin: false,

  // Actions
  signIn: (user: User) => {
    set({ 
      isAuthenticated: true, 
      user, 
      isLoading: false 
    });
  },

  signOut: async () => {
    try {
      await Auth.signOut();
      set({ 
        isAuthenticated: false, 
        user: null, 
        firstTimeSignin: false,
        isLoading: false
      });
    } catch (error) {
      console.error('Sign out error:', error);
      // Even if signOut fails, clear local state
      set({ 
        isAuthenticated: false, 
        user: null, 
        firstTimeSignin: false,
        isLoading: false
      });
    }
  },

  setFirstTimeSignin: (value: boolean) => {
    set({ firstTimeSignin: value });
  },

  clearFirstTimeSignin: () => {
    set({ firstTimeSignin: false });
  },

  enableFirstTimeSignin: () => {
    set({ firstTimeSignin: true });
  },

  checkAuthStatus: async () => {
    set({ isLoading: true });
    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const user: User = {
        id: cognitoUser.username,
        username: cognitoUser.username,
        email: cognitoUser.attributes?.email || '',
        profilePhotoId: cognitoUser.attributes?.picture,
        attributes: cognitoUser.attributes
      };
      
      // Check if this is a first-time social sign-in user
      const isSocialUser = cognitoUser.attributes?.identities && 
        JSON.parse(cognitoUser.attributes.identities).some((identity: any) => 
          identity.providerName === 'Google'
        );
      
      const hasCompletedOnboarding = cognitoUser.attributes?.['custom:onboarding_complete'] === 'true';
      
      if (isSocialUser && !hasCompletedOnboarding) {
        set({ firstTimeSignin: true });
      }
      
      set({ 
        isAuthenticated: true, 
        user, 
        isLoading: false 
      });
    } catch (error) {
      set({ 
        isAuthenticated: false, 
        user: null, 
        isLoading: false 
      });
    }
  },

  completeOnboarding: async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        'custom:onboarding_complete': 'true'
      });
      set({ firstTimeSignin: false });
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  },

  initialize: () => {
    // Set up Auth Hub listener for auth state changes
    const authHandler = (data: any) => {
      const event = data.payload.event;
      const currentState = get();
      
      if (event === "signOut") {
        set({ 
          isAuthenticated: false, 
          user: null, 
          firstTimeSignin: false,
          isLoading: false
        });
      } else if (event === "signIn") {
        // Refresh auth status when sign in event occurs
        get().checkAuthStatus();
      }
    };

    Hub.listen('auth', authHandler);
    
    // Initial auth check
    get().checkAuthStatus();

    // Return cleanup function
    return () => {
      Hub.remove('auth', authHandler);
    };
  },
}));
